/**
 * Колофон ленты: сколько не показали, и только живые причины.
 *
 * Конкурент пишет «найдено 12 216 788». Число значит, что фильтров нет.
 * Короткая лента при этом выглядит одинаково в двух случаях: рынок спокоен
 * или мы срезали девять десятых. Человек, который не может их различить,
 * уходит к тому, у кого «миллион».
 *
 * Подвал отвечает одним взглядом. Слева — сколько не показали с открытия
 * этой вкладки. Справа, в той же колонке, что прибыль строки, — причины с
 * ненулевым счётом: имя влево, число вправо. Нет числа — нет строки. Ноль
 * в таблице читался как «клонов не было», прочерк требовал абзаца.
 *
 * Чего здесь больше нет. Абзацы про клонов, часы и темп: возраст уже на
 * строке, темп — на полосе «может не дожить», клоны отсекаются до рождения
 * вилки и в счёт не входят. Клавиши: они работают, а подпись под лентой
 * после первого дня — учебник, который не читают, когда ставят.
 */

import type { ReactElement } from 'react';

import { tempoOf, useSettings } from '../store/settings';
import { rub } from './format';
import { PAPER_UI, ledger, usePaper } from './paper';
import { hiddenSince, useScreen } from './store';

import './hunt.css';

/** Порядок категорий: сначала то, что зависит от человека, потом от рынка. */
const ORDER = ['tempo', 'book', 'fee', 'clones', 'other'];

/**
 * Имя в разборе — слово, не предложение. Серверные подписи длиннее
 * («съела комиссия или цена неверна»): в таблице из пяти строк их ещё
 * читали, в колофоне они снова становились абзацем.
 */
const SHORT: Record<string, string> = {
  tempo: 'не успеть',
  book: 'нет объёма',
  fee: 'комиссия',
  clones: 'клоны',
  other: 'прочее',
  arithmetic: 'арифметика',
  immaturity: 'наши часы',
  market: 'догадка рынка',
};

const THIN = '\u2009';

/** Целое с отбивкой разрядов: 1621 в подвале читается хуже, чем 1 621. */
const qty = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, THIN);

export function Hunt({ unpriced }: { unpriced: number }): ReactElement {
  const meta = useScreen((s) => s.meta);
  const baseline = useScreen((s) => s.baseline);
  const cut = useScreen((s) => s.cutByFrame);
  const attempts = useSettings((s) => s.attempts);
  const now = useScreen((s) => (PAPER_UI ? s.now : 0));
  const bets = usePaper((s) => s.bets);
  const paperOn = usePaper((s) => s.on);
  const setPaper = usePaper((s) => s.setOn);
  const paper = ledger(bets, tempoOf(attempts), now);

  const since = hiddenSince(meta.hidden, baseline);
  const forksNow = useScreen((s) => s.forks.size);
  const total = Object.values(since).reduce((sum, value) => sum + value, 0);
  const why = ORDER.concat(Object.keys(since).filter((code) => !ORDER.includes(code))).flatMap(
    (code) => {
      const n = since[code];
      if (n === undefined || n <= 0) return [];
      // Hunt-ключи (tempo/fee) — не HIDDEN_GROUP_NAMES сервера. Подпись короткая.
      return [{ code, n, name: SHORT[code] ?? code }];
    },
  );

  const nowWhy: { code: string; n: number; name: string }[] = [];
  const byClass = meta.funnel?.hiddenByClass ?? {};
  for (const code of ['immaturity', 'market', 'arithmetic'] as const) {
    const n = byClass[code];
    if (n !== undefined && n > 0) {
      nowWhy.push({ code, n, name: SHORT[code] ?? code });
    }
  }

  const notes: string[] = [];
  if (unpriced > 0) notes.push(`${qty(unpriced)} без строки`);
  if (cut > 0) notes.push(`в кадр не влезли ${qty(cut)}`);
  if (meta.cutUpstream !== null) {
    notes.push(`источник отдал ${qty(meta.cutUpstream.got)} из ${qty(meta.cutUpstream.matched)}`);
  }
  const clones = meta.funnel?.clonesRejected;
  if (clones !== null && clones !== undefined && clones > 0) {
    notes.push(`клоны отсекли ${qty(clones)} пар до рождения вилки`);
  }

  const emptyHot = forksNow === 0;
  const tabLabel = emptyHot ? 'с открытия вкладки, не причина пустого экрана' : 'с этой вкладки';

  return (
    <footer className="nx-hunt">
      <div className="nx-hunt__read">
        <b className="nx-hunt__n">{qty(total)}</b>
        <span className="nx-hunt__unit">
          не показали
          <i>{tabLabel}</i>
        </span>
      </div>
      {(nowWhy.length > 0 || why.length > 0) && (
        <div className="nx-hunt__aside">
          {nowWhy.length > 0 && (
            <ul className="nx-hunt__why" aria-label="скрыто в текущем такте">
              {nowWhy.map((one) => (
                <li key={`now-${one.code}`}>
                  <span>сейчас {one.name}</span>
                  <b>{qty(one.n)}</b>
                </li>
              ))}
            </ul>
          )}
          {why.length > 0 && (
            <ul className="nx-hunt__why" aria-label="скрыто с открытия вкладки">
              {why.map((one) => (
                <li key={one.code}>
                  <span>{one.name}</span>
                  <b>{qty(one.n)}</b>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {(notes.length > 0 || PAPER_UI) && (
        <div className="nx-hunt__notes">
          {notes.length > 0 && <p className="nx-hunt__edge">{notes.join(' · ')}</p>}
          {PAPER_UI && (
            <p className="nx-hunt__edge">
              {paper.marked === 0 ? (
                paperOn ? (
                  <>
                    бумажный режим включён: отмеченные вилки идут в разбор и денег не касаются.{' '}
                    <button type="button" className="nx-hunt__off" onClick={() => setPaper(false)}>
                      выключить
                    </button>
                  </>
                ) : (
                  <>
                    бумажный режим выключен: отметки считаются вашими настоящими ставками.{' '}
                    <button type="button" className="nx-hunt__off" onClick={() => setPaper(true)}>
                      включить
                    </button>
                  </>
                )
              ) : (
                <>
                  по бумаге отмечено <b>{paper.marked}</b>
                  {paper.open > 0 && `, ${paper.open} ещё в гонке`}: успели бы к <b>{paper.made}</b>,
                  не успели бы к <b>{paper.missed}</b>
                  {paper.bestRub !== null && `; лучшая состоявшаяся на ${rub(paper.bestRub)} ₽`}
                  {paper.missedBest !== null &&
                    `; лучшая упущенная была на ${paper.missedBest.toFixed(2)}%`}
                  . про исполнение это ничего не говорит: мы видим витрину конторы, а её касса нам
                  не видна.
                </>
              )}
            </p>
          )}
        </div>
      )}
    </footer>
  );
}
